<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import MilkdownEditor from '../editor/MilkdownEditor.svelte';
  import EditorToolbar from '../editor/EditorToolbar.svelte';

  // Lazy-load CodeMirror to avoid bloating the main bundle
  const CodeMirrorEditorPromise = import('../editor/CodeMirrorEditor.svelte');
  import ErrorBoundary from '../ErrorBoundary.svelte';
  import BibliographyPanel from '../bibliography/BibliographyPanel.svelte';
  // Import all stores from the unified index
  // This provides both appStore and editorStore exports, plus coordinated actions
  import {
    // From appStore
    selectedDoc,
    selectedProject,
    editingTemplate,
    docs,
    appActions,
    // From editorStore
    editorActions,
    editorState,
    statusMessage,
    hasUnsavedChanges,
    editorContent,
    loadedContent,
    yamlFrontmatter,
    currentDocId,
    currentTemplateId
  } from '../../stores';
  import { editorInstance } from '../../stores/editorStore';
  import { i18n, translations } from '../../stores/i18nStore';
  import type { LanguageCode } from '../../stores/i18nStore';
  import { isZoteroConfigured } from '../../stores/authStore';
  import { totalItems as zoteroTotalItems } from '../../stores/zoteroStore';
  import type { ZoteroItem } from '../../services/zoteroService';
  import { createCitationMarkdown } from '../../plugins/citationPlugin';
  import { exportDocument, checkPandocHealth, type ExportFormat, type ExportOptions } from '../../services/pandocService';
  import { applyEpubTypeToMarkdown } from '../../plugins/epubTypePlugin';
  import { bibliographyService } from '../../services/bibliographyService';
  import { templates } from '../../stores/appStore';
  import { patchPlaceholders, parseProjectMetadata, getPlaceholderMap, resolvePlaceholder, resolveBibliographyPlaceholder } from '../../services/placeholderService';
  import { editorViewCtx } from '@milkdown/kit/core';
  import type { Ctx } from '@milkdown/kit/ctx';
  import { countWords, countCharacters } from '../../utils/textStats';
  import { db } from '../../services/indexedDBService';
  import { getDocumentFilename, normalizeForFilename } from '../../utils/fileNaming';
  import { timeouts } from '../../config';
  import type { DocumentType, DocumentSection } from '../../types';

  // Types
  interface DocumentTypeOption {
    value: string;
    label: string;
  }

  interface DocumentTypeCategory {
    label: string;
    types: DocumentTypeOption[];
  }

  type DocumentTypesConfig = Record<string, DocumentTypeCategory>;

  // Source-only mode: non-markdown templates (CSS, Lua, CSL) bypass WYSIWYG
  const SOURCE_ONLY_TYPES = ['css', 'lua', 'csl', 'other'] as const;

  function getTemplateTypeIcon(type: string): string {
    switch (type) {
      case 'css': return '🎨';
      case 'lua': return '🌙';
      case 'csl': return '📚';
      default: return '📄';
    }
  }

  function getTemplateTypeLabel(type: string): string {
    switch (type) {
      case 'css': return 'CSS';
      case 'lua': return 'Lua';
      case 'csl': return 'CSL';
      default: return type.toUpperCase();
    }
  }

  // State
  let isFullscreen: boolean = false;
  let showMarkdownSource: boolean = false;
  let markdownSourceText: string = '';
  let sourceViewDocId: string | null = null; // tracks which doc the source view is showing
  let showExportMenu: boolean = false;
  let isExporting: boolean = false;
  let isSplitting: boolean = false;
  let isEditingShorttitle: boolean = false;
  let editedShorttitle: string = '';
  let shorttitleInputRef: HTMLInputElement;
  let showNavigator: boolean = false;
  let navigatorTab: 'toc' | 'style' | 'placeholders' | 'bibliography' = 'toc';
  let styleSubTab: 'paragraph' | 'character' = 'paragraph';

  // TOC types
  interface TocHeading {
    level: number;
    text: string;
    id: string;
  }

  // Custom div block types
  interface DivBlock {
    name: string;
    preview: string;
    id: string;
    lineNumber: number;
  }

  // Inline style types (for [text]{.class} patterns)
  interface InlineStyle {
    className: string;
    text: string;
    id: string;
    count: number;
  }

  // Styles handled by toolbar - exclude from Navigator
  const TOOLBAR_STYLES = ['underline', 'smallcaps'];

  // Structure des types de documents
  const DOCUMENT_TYPES: DocumentTypesConfig = {
    bodymatter: {
      label: 'Bodymatter',
      types: [
        { value: 'chapter', label: 'Chapter' },
        { value: 'part', label: 'Part' }
      ]
    },
    frontmatter: {
      label: 'Frontmatter',
      types: [
        { value: 'titlepage', label: 'Title Page' },
        { value: 'halftitlepage', label: 'Half Title Page' },
        { value: 'copyright-page', label: 'Copyright Page' },
        { value: 'dedication', label: 'Dedication' },
        { value: 'prologue', label: 'Prologue' },
        { value: 'abstract', label: 'Abstract' },
        { value: 'acknowledgments', label: 'Acknowledgments' },
        { value: 'credits', label: 'Credits' },
        { value: 'other-credits', label: 'Other Credits' },
        { value: 'keywords', label: 'Keywords' },
        { value: 'imprint', label: 'Imprint' },
        { value: 'contributors', label: 'Contributors' },
        { value: 'errata', label: 'Errata' },
        { value: 'revision-history', label: 'Revision History' },
        { value: 'seriespage', label: 'Series Page' },
        { value: 'foreword', label: 'Foreword' },
        { value: 'preface', label: 'Preface' },
        { value: 'frontispiece', label: 'Frontispiece' },
        { value: 'introduction', label: 'Introduction' }
      ]
    },
    backmatter: {
      label: 'Backmatter',
      types: [
        { value: 'appendix', label: 'Appendix' },
        { value: 'colophon', label: 'Colophon' },
        { value: 'bibliography', label: 'Bibliography' },
        { value: 'index', label: 'Index' },
        { value: 'conclusion', label: 'Conclusion' },
        { value: 'annexe', label: 'Annexe' },
        { value: 'postface', label: 'Postface' }
      ]
    }
  };

  // Obtenir la catégorie d'un type de document
  function getDocumentCategory(documentType: string | null | undefined): string | null {
    if (!documentType) return null;

    for (const [category, data] of Object.entries(DOCUMENT_TYPES)) {
      if (data.types.some(t => t.value === documentType)) {
        return category;
      }
    }
    return null;
  }

  // Charger le document quand la sélection change
  // La logique est centralisée dans editorStore.selectDoc()
  $: editorActions.selectDoc($selectedDoc);

  // When switching documents while in source view, update the source text
  // to avoid showing stale content from the previous document.
  // loadedContent is set before currentDocId in loadDoc(), so it's already correct here.
  $: if (showMarkdownSource && $currentDocId && $currentDocId !== sourceViewDocId) {
    sourceViewDocId = $currentDocId;
    markdownSourceText = $loadedContent || '';
  }

  // Source-only mode for non-markdown templates (CSS, Lua, CSL)
  $: isSourceOnlyMode = $editingTemplate != null
    && SOURCE_ONLY_TYPES.includes($editingTemplate.templateType as typeof SOURCE_ONLY_TYPES[number]);

  // When entering source-only mode, initialize source text from loaded content
  $: if (isSourceOnlyMode && $editingTemplate) {
    markdownSourceText = $editorContent || $loadedContent || '';
    sourceViewDocId = $currentDocId;
  }

  async function handleSave(): Promise<void> {
    try {
      await editorActions.saveDoc();
    } catch (error) {
      console.error('Failed to save doc:', error);
    }
  }

  function toggleFullscreen(): void {
    isFullscreen = !isFullscreen;
  }

  /**
   * Extraire le premier titre H1 du contenu markdown
   */
  function extractFirstH1(markdown: string): string | null {
    if (!markdown) return null;

    // Regex pour trouver le premier # Titre (avec un seul #)
    const h1Match = markdown.match(/^#\s+(.+)$/m);

    if (h1Match && h1Match[1]) {
      // Strip Pandoc attribute blocks {#id .class key=value} from end
      return h1Match[1].replace(/\s*\{[^}]+\}\s*$/, '').trim();
    }

    return null;
  }

  /**
   * Mettre à jour automatiquement le titre du document depuis le premier H1
   */
  async function updateTitleFromH1(content: string): Promise<void> {
    if (!$selectedDoc) return;

    // Si le titre a été modifié manuellement, ne pas l'écraser
    if ($selectedDoc.titleManuallySet) return;

    const h1Title = extractFirstH1(content);

    if (h1Title && h1Title !== $selectedDoc.title) {
      console.log(`📝 Auto-updating title from H1: "${h1Title}"`);
      try {
        await appActions.updateDoc($selectedDoc.id, {
          title: h1Title,
          titleManuallySet: false
        });
      } catch (error) {
        console.error('Failed to auto-update title:', error);
      }
    }
  }

  /**
   * Mettre à jour les statistiques du document (wordCount, charCount)
   * Appelé lors de la sauvegarde automatique
   */
  async function updateDocumentStats(content: string): Promise<void> {
    if (!$selectedDoc) return;

    const newWordCount = countWords(content);
    const newCharCount = countCharacters(content);

    // Mettre à jour seulement si les nombres ont changé
    if (newWordCount !== $selectedDoc.wordCount || newCharCount !== $selectedDoc.charCount) {
      try {
        await appActions.updateDoc($selectedDoc.id, {
          wordCount: newWordCount,
          charCount: newCharCount
        });
      } catch (error) {
        console.error('Failed to update document stats:', error);
      }
    }
  }

  // Timer pour le debounce de la mise à jour du titre
  let updateTitleTimer: ReturnType<typeof setTimeout>;
  let lastSavedContent: string = '';

  // Surveiller les changements de contenu pour extraire le H1
  $: {
    if ($editorContent && $selectedDoc && !$selectedDoc.titleManuallySet) {
      // Mise à jour du titre si pas modifié manuellement
      if (updateTitleTimer) {
        clearTimeout(updateTitleTimer);
      }

      updateTitleTimer = setTimeout(() => {
        updateTitleFromH1($editorContent);
      }, timeouts.titleUpdateDebounce);
    }
  }

  // Surveiller les sauvegardes pour mettre à jour les stats (wordCount, charCount)
  $: {
    if ($editorState.lastSaved && $editorContent && $selectedDoc) {
      // Si le contenu a changé depuis la dernière fois qu'on a mis à jour les stats
      if ($editorContent !== lastSavedContent) {
        lastSavedContent = $editorContent;
        updateDocumentStats($editorContent);
      }
    }
  }

  function toggleMarkdownSource(): void {
    if (!showMarkdownSource) {
      // Passer en mode source : capturer le contenu actuel
      markdownSourceText = $editorContent || $loadedContent;
      sourceViewDocId = $currentDocId;
    } else {
      // Revenir en mode WYSIWYG : mettre à jour le contenu
      // On doit mettre à jour loadedContent pour que l'éditeur Milkdown se recharge avec le nouveau contenu
      editorContent.set(markdownSourceText);
      loadedContent.set(markdownSourceText);
    }
    showMarkdownSource = !showMarkdownSource;
  }

  function toggleNavigator(): void {
    showNavigator = !showNavigator;
  }

  /**
   * Extract headings (H1-H4) from markdown content
   */
  function extractHeadings(markdown: string): TocHeading[] {
    if (!markdown) return [];

    const headings: TocHeading[] = [];
    const lines = markdown.split('\n');
    let headingIndex = 0;

    for (const line of lines) {
      // Match ATX-style headings: # to ####
      const match = line.match(/^(#{1,4})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        let text = match[2].trim();
        // Remove any trailing heading attributes like {#custom-id .class}
        text = text.replace(/\s*\{[^}]*\}\s*$/, '').trim();
        // Generate a simple ID for scrolling
        const id = `heading-${headingIndex++}`;
        headings.push({ level, text, id });
      }
    }

    return headings;
  }

  /**
   * Extract custom div blocks (:::name) from markdown content
   */
  function extractDivBlocks(markdown: string): DivBlock[] {
    if (!markdown) return [];

    const blocks: DivBlock[] = [];
    const lines = markdown.split('\n');
    let blockIndex = 0;
    let insideBlock = false;
    let currentBlockName = '';
    let currentBlockPreview = '';
    let currentBlockLineNumber = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Match opening fence: :::name or ::: {.name}
      const openMatch = line.match(/^:::(\s*\{\.([a-zA-Z0-9_-]+)\}|\s*([a-zA-Z0-9_-]+))/);
      if (openMatch && !insideBlock) {
        insideBlock = true;
        currentBlockName = openMatch[2] || openMatch[3] || 'div';
        currentBlockLineNumber = i;
        currentBlockPreview = '';
        continue;
      }

      // Match closing fence: :::
      if (line.trim() === ':::' && insideBlock) {
        blocks.push({
          name: currentBlockName,
          preview: currentBlockPreview.trim().substring(0, 50) + (currentBlockPreview.length > 50 ? '...' : ''),
          id: `block-${blockIndex++}`,
          lineNumber: currentBlockLineNumber
        });
        insideBlock = false;
        currentBlockName = '';
        currentBlockPreview = '';
        continue;
      }

      // Capture content inside block for preview
      if (insideBlock && currentBlockPreview.length < 60) {
        currentBlockPreview += line + ' ';
      }
    }

    return blocks;
  }

  /**
   * Extract inline styles ([text]{.class}) from markdown content
   * Excludes styles handled by toolbar (underline, smallcaps)
   */
  function extractInlineStyles(markdown: string): InlineStyle[] {
    if (!markdown) return [];

    // Match [text]{.class} or [text]{.class1 .class2}
    const regex = /\[([^\]]+)\]\{\.([a-zA-Z0-9_-]+)(?:\s+[^}]*)?\}/g;
    const styleMap = new Map<string, { texts: string[], count: number }>();
    let styleIndex = 0;

    let match;
    while ((match = regex.exec(markdown)) !== null) {
      const text = match[1];
      const className = match[2];

      // Skip toolbar-handled styles
      if (TOOLBAR_STYLES.includes(className)) continue;

      if (styleMap.has(className)) {
        const entry = styleMap.get(className)!;
        entry.count++;
        if (entry.texts.length < 3) {
          entry.texts.push(text);
        }
      } else {
        styleMap.set(className, { texts: [text], count: 1 });
      }
    }

    // Convert map to array
    const styles: InlineStyle[] = [];
    for (const [className, data] of styleMap) {
      styles.push({
        className,
        text: data.texts.join(', '),
        id: `style-${styleIndex++}`,
        count: data.count
      });
    }

    return styles;
  }

  // Compute TOC headings from current content
  $: tocHeadings = extractHeadings(showMarkdownSource ? markdownSourceText : ($editorContent || $loadedContent || ''));

  // Compute div blocks from current content
  $: divBlocks = extractDivBlocks(showMarkdownSource ? markdownSourceText : ($editorContent || $loadedContent || ''));

  // Compute inline styles from current content
  $: inlineStyles = extractInlineStyles(showMarkdownSource ? markdownSourceText : ($editorContent || $loadedContent || ''));

  // Placeholder list for navigator
  interface PlaceholderItem {
    name: string;
    token: string;
    value: string;
    category: string;
  }

  const PLACEHOLDER_CATEGORIES: Record<string, { label: string; items: string[] }> = {
    general: {
      label: 'General',
      items: ['TITLE', 'SUBTITLE', 'ORIGINAL_TITLE', 'LANG', 'DATE']
    },
    people: {
      label: 'Auteurs',
      items: ['AUTHOR', 'TRANSLATOR', 'CRITICAL']
    },
    publication: {
      label: 'Publication',
      items: ['PUBLISHER', 'PLACE', 'SERIE', 'PRICE', 'PUBLISHED_PRINT', 'SOURCE']
    },
    identifiers: {
      label: 'Identifiants',
      items: ['ISBN_EBOOK', 'ISBN_PRINT', 'ISSN']
    },
    content: {
      label: 'Contenu',
      items: ['DESCRIPTION', 'DEDICATION', 'COVER_NOTE', 'RIGHTS', 'FUNDING', 'EDITIONS', 'ACCESSIBILITY_SUMMARY']
    }
  };

  $: placeholderItems = (() => {
    const meta = parseProjectMetadata($selectedProject?.metadata);
    const map = getPlaceholderMap();
    const items: PlaceholderItem[] = [];
    for (const [catKey, cat] of Object.entries(PLACEHOLDER_CATEGORIES)) {
      for (const name of cat.items) {
        if (!map[name]) continue;
        items.push({
          name,
          token: `{{${name}}}`,
          value: map[name](meta),
          category: cat.label
        });
      }
    }
    // Add custom fields as a dynamic category
    for (const [yamlKey, value] of Object.entries(meta.customFields)) {
      const placeholderName = yamlKey.toUpperCase().replace(/-/g, '_');
      items.push({
        name: placeholderName,
        token: `{{${placeholderName}}}`,
        value,
        category: 'Personnalise'
      });
    }
    return items;
  })();

  $: placeholderCount = placeholderItems.filter(p => p.value).length;

  // Unique category labels in order (built-in first, then custom)
  $: placeholderCategoryLabels = (() => {
    const seen = new Set<string>();
    const labels: string[] = [];
    for (const item of placeholderItems) {
      if (!seen.has(item.category)) {
        seen.add(item.category);
        labels.push(item.category);
      }
    }
    return labels;
  })();

  /**
   * Insert a placeholder token at the current cursor position in the editor
   */
  function insertPlaceholder(token: string): void {
    const editor = $editorInstance;
    if (!editor) return;

    try {
      editor.action((ctx: Ctx) => {
        const view = ctx.get(editorViewCtx);
        const { state, dispatch } = view;
        const { from } = state.selection;
        const tr = state.tr.insertText(token, from);
        dispatch(tr);
        view.focus();
      });
    } catch (error) {
      console.error('Failed to insert placeholder:', error);
    }
  }

  /**
   * Insert a citation at the current cursor position in the editor
   */
  function insertCitation(key: string): void {
    const editor = $editorInstance;
    if (!editor) return;

    const citationText = createCitationMarkdown(key);

    try {
      editor.action((ctx: Ctx) => {
        const view = ctx.get(editorViewCtx);
        const { state, dispatch } = view;
        const { from } = state.selection;
        const tr = state.tr.insertText(citationText, from);
        dispatch(tr);
        view.focus();
      });
    } catch (error) {
      console.error('Failed to insert citation:', error);
    }
  }

  /**
   * Handle citation insertion event from BibliographyPanel
   */
  function handleInsertCitation(event: CustomEvent<{ key: string; item: ZoteroItem }>) {
    insertCitation(event.detail.key);
  }

  /**
   * Scroll to a heading in the editor
   */
  function scrollToHeading(heading: TocHeading): void {
    // Find the heading in the DOM by its text content
    const editorContainer = document.querySelector('.editor-container .milkdown');
    if (!editorContainer) return;

    // Find all heading elements (h1-h4)
    const headingElements = editorContainer.querySelectorAll('h1, h2, h3, h4');
    let headingCount = 0;

    for (const element of headingElements) {
      const elementLevel = parseInt(element.tagName.substring(1));
      const elementText = element.textContent?.trim() || '';

      // Match by level and text
      if (elementLevel === heading.level && elementText === heading.text) {
        // Check if this is the right occurrence by counting previous matches
        const matchIndex = tocHeadings.findIndex(h => h.id === heading.id);
        const previousMatches = tocHeadings.slice(0, matchIndex).filter(
          h => h.level === heading.level && h.text === heading.text
        ).length;

        if (headingCount === previousMatches) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Add a brief highlight effect
          element.classList.add('toc-highlight');
          setTimeout(() => element.classList.remove('toc-highlight'), 1500);
          return;
        }
        headingCount++;
      }
    }
  }

  /**
   * Scroll to a div block in the editor
   */
  function scrollToDivBlock(block: DivBlock): void {
    const editorContainer = document.querySelector('.editor-container .milkdown');
    if (!editorContainer) return;

    // Find all div elements with the block's class
    const divElements = editorContainer.querySelectorAll(`div.${block.name}, section.${block.name}`);

    // Find the correct occurrence based on index
    const blockIndex = divBlocks.filter(b => b.name === block.name).findIndex(b => b.id === block.id);

    if (divElements[blockIndex]) {
      const element = divElements[blockIndex];
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Add a brief highlight effect
      element.classList.add('toc-highlight');
      setTimeout(() => element.classList.remove('toc-highlight'), 1500);
    }
  }

  /**
   * Scroll to first occurrence of an inline style in the editor
   */
  function scrollToInlineStyle(style: InlineStyle): void {
    const editorContainer = document.querySelector('.editor-container .milkdown');
    if (!editorContainer) return;

    // Find span elements with the style's class
    const spanElements = editorContainer.querySelectorAll(`span.${style.className}, span[data-type="text-span"].${style.className}`);

    if (spanElements.length > 0) {
      const element = spanElements[0];
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a brief highlight effect
      element.classList.add('toc-highlight');
      setTimeout(() => element.classList.remove('toc-highlight'), 1500);
    }
  }

  // Extract shorttitle from YAML frontmatter reactively
  $: currentShorttitle = (() => {
    const fm = $yamlFrontmatter;
    if (!fm) return '';
    const match = fm.match(/^shorttitle:\s*(.*)$/m);
    return match ? match[1].trim().replace(/^['"]|['"]$/g, '') : '';
  })();

  function startEditingShorttitle(): void {
    if (!$selectedDoc) return;
    isEditingShorttitle = true;
    editedShorttitle = currentShorttitle;
    setTimeout(() => {
      if (shorttitleInputRef) {
        shorttitleInputRef.focus();
        shorttitleInputRef.select();
      }
    }, 10);
  }

  function saveShorttitle(): void {
    const newValue = editedShorttitle.trim();
    const oldFm = $yamlFrontmatter;
    let newFm: string;

    if (oldFm) {
      // Update existing shorttitle field or add it
      if (/^shorttitle:\s*.*$/m.test(oldFm)) {
        newFm = oldFm.replace(/^shorttitle:\s*.*$/m, `shorttitle: ${newValue}`);
      } else {
        // Insert before the closing ---
        newFm = oldFm.replace(/^(---\n?)$/m, `shorttitle: ${newValue}\n$1`);
      }
    } else {
      // Create frontmatter from scratch
      newFm = `---\nshorttitle: ${newValue}\n---\n`;
    }

    yamlFrontmatter.set(newFm);

    // Reconstruct full content with updated frontmatter and trigger save
    const content = $editorContent;
    let body: string;
    if (oldFm && content.startsWith(oldFm)) {
      body = content.slice(oldFm.length);
      if (body.startsWith('\n')) body = body.slice(1);
    } else {
      body = content;
    }
    const newContent = newFm + '\n' + body;
    editorContent.set(newContent);

    if ($currentDocId) {
      editorActions.saveDoc($currentDocId, newContent);
    }

    isEditingShorttitle = false;
  }

  function cancelEditShorttitle(): void {
    isEditingShorttitle = false;
    editedShorttitle = '';
  }

  function handleShorttitleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveShorttitle();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditShorttitle();
    }
  }

  /**
   * Get the project language from metadata, falling back to 'fr'
   */
  function getProjectLang(): LanguageCode {
    const meta = parseProjectMetadata($selectedProject?.metadata);
    const lang = meta.lang?.slice(0, 2)?.toLowerCase();
    return (lang === 'en' || lang === 'fr') ? lang as LanguageCode : 'fr';
  }

  /**
   * Translate a docType key using the project language (not UI language)
   */
  function translateDocType(docType: string): string {
    const lang = getProjectLang();
    const key = `docType.${docType}`;
    return translations[lang]?.[key] || translations['en']?.[key] || docType;
  }

  /**
   * Translate a docType with occurrence number if multiple docs share the same type.
   */
  function translateDocTypeWithNumber(docType: string, docId: string): string {
    const label = translateDocType(docType);
    // Count docs of same type, sorted by section then order (same logic as fileNaming)
    const sameTypeDocs = $docs
      .filter(d => d.documentType === docType)
      .sort((a, b) => {
        const sectionOrder = ['frontmatter', 'bodymatter', 'backmatter'];
        const sA = sectionOrder.indexOf(a.documentSection || 'bodymatter');
        const sB = sectionOrder.indexOf(b.documentSection || 'bodymatter');
        if (sA !== sB) return sA - sB;
        const oA = a.order ?? Infinity;
        const oB = b.order ?? Infinity;
        if (oA !== oB) return oA - oB;
        return (a.title || '').localeCompare(b.title || '');
      });

    if (sameTypeDocs.length > 1) {
      const occurrence = sameTypeDocs.findIndex(d => d.id === docId) + 1;
      return `${label} ${occurrence}`;
    }
    return label;
  }

  /**
   * Check if a shorttitle value is an auto-generated docType label (in any language),
   * with or without a trailing number (e.g. "Chapitre", "Chapitre 1", "Chapter 3")
   */
  function isAutoGeneratedShorttitle(value: string): boolean {
    if (!value) return true;
    // Strip trailing number: "Chapitre 3" -> "Chapitre"
    const baseValue = value.replace(/\s+\d+$/, '');
    return Object.keys(DOCUMENT_TYPES).some(cat =>
      DOCUMENT_TYPES[cat].types.some((t: DocumentTypeOption) =>
        Object.keys(translations).some(lang =>
          translations[lang as LanguageCode]?.[`docType.${t.value}`] === baseValue
        )
      )
    );
  }

  async function handleDocumentTypeChange(event: Event): Promise<void> {
    if (!$selectedDoc) return;

    const target = event.target as HTMLSelectElement;
    const newType = target.value;
    const category = getDocumentCategory(newType);

    try {
      await appActions.updateDoc($selectedDoc.id, {
        documentType: (newType || null) as DocumentType | null,
        documentSection: (category || null) as DocumentSection | null  // Déplacer automatiquement dans la bonne section
      });

      // Auto-set shorttitle from document type (in project language) if not already customized
      if (newType) {
        const typeLabel = translateDocTypeWithNumber(newType, $selectedDoc.id);
        const oldFm = $yamlFrontmatter;
        let newFm: string;

        if (oldFm) {
          if (/^shorttitle:\s*.*$/m.test(oldFm)) {
            // Only overwrite if current shorttitle is empty or was a previous auto-set docType label
            if (isAutoGeneratedShorttitle(currentShorttitle)) {
              newFm = oldFm.replace(/^shorttitle:\s*.*$/m, `shorttitle: ${typeLabel}`);
            } else {
              newFm = oldFm; // Keep custom shorttitle
            }
          } else {
            newFm = oldFm.replace(/^(---\n?)$/m, `shorttitle: ${typeLabel}\n$1`);
          }
        } else {
          newFm = `---\nshorttitle: ${typeLabel}\n---\n`;
        }

        if (newFm !== oldFm) {
          yamlFrontmatter.set(newFm);

          // Persist to content
          const content = $editorContent;
          let body: string;
          if (oldFm && content.startsWith(oldFm)) {
            body = content.slice(oldFm.length);
            if (body.startsWith('\n')) body = body.slice(1);
          } else {
            body = content;
          }
          // Apply epub:type attributes to first H1
          const newContent = applyEpubTypeToMarkdown(newFm + '\n' + body, newType);
          editorContent.set(newContent);
          editorActions.saveDoc($currentDocId, newContent);
        }
      }

      console.log(`✅ Document type updated: ${newType} - moved to ${category} section`);
    } catch (error) {
      console.error('Failed to update document type:', error);
      alert($i18n('notes.errorUpdatingType'));
    }
  }

  function handleSourceUpdate(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    markdownSourceText = target.value;
    // Marquer comme modifié
    editorContent.set(markdownSourceText);
  }

  function handleSourceOnlyChange(newContent: string): void {
    markdownSourceText = newContent;
    editorContent.set(newContent);
  }

  async function saveFromSource(): Promise<void> {
    try {
      if ($currentTemplateId) {
        await editorActions.saveTemplate(markdownSourceText);
      } else if ($selectedDoc) {
        await editorActions.saveDoc($selectedDoc.id, markdownSourceText);
      }
    } catch (error) {
      console.error('Failed to save from source:', error);
    }
  }

  // Export du document
  function toggleExportMenu(): void {
    showExportMenu = !showExportMenu;
  }

  function _closeExportMenu(): void {
    showExportMenu = false;
  }

  async function handleExport(format: ExportFormat): Promise<void> {
    if (!$selectedDoc) return;

    isExporting = true;
    showExportMenu = false;

    try {
      const rawContent = showMarkdownSource ? markdownSourceText : ($editorContent || $loadedContent);
      // Resolve {{PLACEHOLDER}} tokens with project metadata before export
      let content = patchPlaceholders(rawContent, $selectedProject?.metadata);

      // Get CSL style from templates (needed for both bibliography placeholder and citations)
      const cslTemplate = $templates.find(
        (t) => t.templateSection === 'csl' || t.templateType === 'csl' || t.filename?.toLowerCase().includes('.csl')
      );

      // Resolve {{bibliography}} placeholder (generates plain text bibliography from cited items)
      if ($selectedProject) {
        content = await resolveBibliographyPlaceholder(content, $selectedProject.id, cslTemplate?.content);
      }

      // Utiliser le nom de fichier structuré (ex: "02-chapter-1.md" -> "02-chapter-1")
      // Si le document n'a pas de type défini, fallback sur le titre normalisé
      const structuredFilename = getDocumentFilename($selectedDoc, $docs);
      const filename = structuredFilename
        ? structuredFilename.replace(/\.md$/, '')  // Retirer l'extension .md
        : normalizeForFilename($selectedDoc.title || 'document');

      // Vérifier que Pandoc est disponible (tous les formats passent par Pandoc)
      await checkPandocHealth();

      // Build export options with optional bibliography/CSL
      const exportOptions: ExportOptions = {};

      // Check if document has citations ([@key] pattern)
      const hasCitations = /\[@[a-zA-Z0-9_:-]+/.test(content);

      if (hasCitations && $selectedProject && cslTemplate?.content) {
        // Get bibliography items in CSL JSON format
        const bibItems = await bibliographyService.getAllItemsAsCSL($selectedProject.id);

        if (bibItems && bibItems.length > 0) {
          exportOptions.bibliography = bibItems;
          exportOptions.csl = cslTemplate.content;
          console.log(`📚 Export with citeproc: ${bibItems.length} items, CSL style: ${cslTemplate.name}`);
        }
      }

      await exportDocument(content, filename, format, exportOptions);
      console.log(`✅ Export ${format} réussi: ${filename}.${format}`);
    } catch (error) {
      console.error('Failed to export:', error);
      alert($i18n('notes.exportError', { message: (error as Error).message }));
    } finally {
      isExporting = false;
    }
  }

  /**
   * Extract footnote definitions from markdown content
   * Returns a map of footnote id -> definition text (including the [^id]: prefix)
   */
  function extractFootnoteDefinitions(content: string): Map<string, string> {
    const footnotes = new Map<string, string>();
    const lines = content.split('\n');
    let currentFootnoteId: string | null = null;
    let currentFootnoteLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Match footnote definition start: [^id]: content
      const footnoteMatch = line.match(/^\[\^([^\]]+)\]:\s*(.*)/);

      if (footnoteMatch) {
        // Save previous footnote if exists
        if (currentFootnoteId) {
          footnotes.set(currentFootnoteId, currentFootnoteLines.join('\n'));
        }
        // Start new footnote
        currentFootnoteId = footnoteMatch[1];
        currentFootnoteLines = [line];
      } else if (currentFootnoteId && (line.startsWith('    ') || line.startsWith('\t') || line.trim() === '')) {
        // Continuation of multi-line footnote (indented or blank line)
        currentFootnoteLines.push(line);
      } else if (currentFootnoteId) {
        // End of footnote - non-indented, non-empty line
        footnotes.set(currentFootnoteId, currentFootnoteLines.join('\n'));
        currentFootnoteId = null;
        currentFootnoteLines = [];
      }
    }

    // Don't forget the last footnote
    if (currentFootnoteId) {
      footnotes.set(currentFootnoteId, currentFootnoteLines.join('\n'));
    }

    return footnotes;
  }

  /**
   * Find all footnote references in content (e.g., [^1], [^note-name])
   */
  function findFootnoteReferences(content: string): Set<string> {
    const refs = new Set<string>();
    // Match [^id] but not [^id]: (which is a definition)
    const regex = /\[\^([^\]]+)\](?!:)/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      refs.add(match[1]);
    }
    return refs;
  }

  /**
   * Remove footnote definitions from content
   */
  function removeFootnoteDefinitions(content: string): string {
    const lines = content.split('\n');
    const resultLines: string[] = [];
    let inFootnote = false;

    for (const line of lines) {
      const footnoteMatch = line.match(/^\[\^([^\]]+)\]:\s*(.*)/);

      if (footnoteMatch) {
        inFootnote = true;
      } else if (inFootnote && (line.startsWith('    ') || line.startsWith('\t') || line.trim() === '')) {
        // Still in multi-line footnote, skip
      } else {
        inFootnote = false;
        resultLines.push(line);
      }
    }

    return resultLines.join('\n');
  }

  /**
   * Renumber footnotes in markdown content sequentially (1, 2, 3...)
   * Based on order of appearance of references in the text
   */
  function renumberFootnotesInMarkdown(content: string): string {
    // Find all footnote references in order of appearance
    const refRegex = /\[\^([^\]]+)\](?!:)/g;
    const refsInOrder: string[] = [];
    const seenRefs = new Set<string>();
    let match;

    while ((match = refRegex.exec(content)) !== null) {
      const label = match[1];
      if (!seenRefs.has(label)) {
        seenRefs.add(label);
        refsInOrder.push(label);
      }
    }

    if (refsInOrder.length === 0) {
      return content;
    }

    // Create mapping: old label -> new number
    const labelMapping = new Map<string, string>();
    refsInOrder.forEach((label, index) => {
      labelMapping.set(label, String(index + 1));
    });

    // Replace references: [^oldLabel] -> [^newNumber]
    let result = content.replace(/\[\^([^\]]+)\](?!:)/g, (match, label) => {
      const newLabel = labelMapping.get(label);
      return newLabel ? `[^${newLabel}]` : match;
    });

    // Replace definitions: [^oldLabel]: -> [^newNumber]:
    result = result.replace(/\[\^([^\]]+)\]:/g, (match, label) => {
      const newLabel = labelMapping.get(label);
      return newLabel ? `[^${newLabel}]:` : match;
    });

    return result;
  }

  /**
   * Split document by H1 headings
   * Each H1 section becomes a new document
   */
  async function handleSplitDocument(): Promise<void> {
    if (!$selectedDoc || !$selectedProject) return;

    const content = showMarkdownSource ? markdownSourceText : ($editorContent || $loadedContent);
    if (!content) return;

    // Extract all footnote definitions from the entire document
    const allFootnotes = extractFootnoteDefinitions(content);

    // Remove footnote definitions from content for parsing sections
    const contentWithoutFootnotes = removeFootnoteDefinitions(content);

    // Parse markdown to find H1 headings and their positions
    const lines = contentWithoutFootnotes.split('\n');
    const sections: { title: string; content: string }[] = [];
    let currentSection: { title: string; lines: string[] } | null = null;
    let preambleLines: string[] = []; // Content before first H1

    for (const line of lines) {
      const h1Match = line.match(/^#\s+(.+)$/);
      if (h1Match) {
        // Save previous section if exists
        if (currentSection) {
          sections.push({
            title: currentSection.title,
            content: currentSection.lines.join('\n').trim()
          });
        }
        // Start new section
        currentSection = {
          title: h1Match[1].replace(/\s*\{[^}]+\}\s*$/, '').trim(),
          lines: [line]
        };
      } else if (currentSection) {
        currentSection.lines.push(line);
      } else {
        // Collect lines before first H1 (preamble)
        preambleLines.push(line);
      }
    }

    // Don't forget the last section
    if (currentSection) {
      sections.push({
        title: currentSection.title,
        content: currentSection.lines.join('\n').trim()
      });
    }

    // Prepend preamble content to the first section if it exists
    if (preambleLines.length > 0 && sections.length > 0) {
      const preamble = preambleLines.join('\n').trim();
      if (preamble) {
        sections[0].content = preamble + '\n\n' + sections[0].content;
      }
    }

    // Add relevant footnotes to each section and renumber them
    for (let i = 0; i < sections.length; i++) {
      const referencedFootnotes = findFootnoteReferences(sections[i].content);
      if (referencedFootnotes.size > 0) {
        const footnoteDefinitions: string[] = [];
        for (const footnoteId of referencedFootnotes) {
          const definition = allFootnotes.get(footnoteId);
          if (definition) {
            footnoteDefinitions.push(definition);
          }
        }
        if (footnoteDefinitions.length > 0) {
          sections[i].content = sections[i].content + '\n\n' + footnoteDefinitions.join('\n\n');
        }
        // Renumber footnotes sequentially (1, 2, 3...) for this section
        sections[i].content = renumberFootnotesInMarkdown(sections[i].content);
      }
    }

    // Check if we have sections to create
    if (sections.length === 0) {
      alert($i18n('notes.splitNoH1'));
      return;
    }

    // Confirm with user
    if (!confirm($i18n('notes.splitConfirm', { count: sections.length }))) {
      return;
    }

    isSplitting = true;

    try {
      // Get current document's order and section for positioning new docs
      const baseOrder = $selectedDoc.order ?? 0;
      const documentSection = $selectedDoc.documentSection;
      const documentType = $selectedDoc.documentType;

      // Store original doc ID before creating new docs
      const originalDocId = $selectedDoc.id;

      // Create new documents for each section
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];

        // Create the document
        const newDoc = await appActions.createDoc({
          projectId: $selectedProject.id,
          title: section.title,
          status: 'todo',
          documentType: documentType,
          documentSection: documentSection,
          order: baseOrder + i + 1,
          wordCount: countWords(section.content),
          charCount: countCharacters(section.content)
        });

        // Save the content
        await db.contents.set(newDoc.id, section.content);

        console.log(`✅ Created split document: "${section.title}"`);
      }

      // Delete the original document
      appActions.selectDoc(null); // Deselect first to avoid UI issues
      await appActions.deleteDoc(originalDocId);
      console.log(`🗑️ Deleted original document`);

      console.log(`✅ Split complete: ${sections.length} documents created`);
      alert($i18n('notes.splitSuccess', { count: sections.length }));

    } catch (error) {
      console.error('Failed to split document:', error);
      alert($i18n('notes.splitError', { message: (error as Error).message }));
    } finally {
      isSplitting = false;
    }
  }

  // Raccourcis clavier
  function handleKeydown(event: KeyboardEvent): void {
    // Cmd/Ctrl + S pour sauvegarder
    if ((event.metaKey || event.ctrlKey) && event.key === 's') {
      event.preventDefault();
      if (isSourceOnlyMode || showMarkdownSource) {
        saveFromSource();
      } else {
        handleSave();
      }
    }

    // Cmd/Ctrl + Shift + F pour plein écran
    if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'f') {
      event.preventDefault();
      toggleFullscreen();
    }

    // Cmd/Ctrl + Shift + M pour basculer vue source (pas en source-only mode)
    if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'm' && !isSourceOnlyMode) {
      event.preventDefault();
      toggleMarkdownSource();
    }
  }

  // Fermer le menu export quand on clique ailleurs
  function handleClickOutside(event: MouseEvent): void {
    if (showExportMenu) {
      const target = event.target as HTMLElement;
      const exportDropdown = target.closest('.export-dropdown');
      if (!exportDropdown) {
        showExportMenu = false;
      }
    }
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('click', handleClickOutside);
    };
  });

  onDestroy(() => {
    // Nettoyer le timer
    if (updateTitleTimer) {
      clearTimeout(updateTitleTimer);
    }
  });
</script>

<div class="notes-panel" class:fullscreen={isFullscreen}>
  <!-- Header avec toolbar -->
  <div class="notes-header">
      <div class="header-left">
        {#if $editingTemplate}
          <h2 class="doc-title-editable template-title">
            {#if isSourceOnlyMode}
              <span class="source-type-badge">{getTemplateTypeLabel($editingTemplate.templateType)}</span>
            {:else}
              <span class="template-badge">📝 Template</span>
            {/if}
            {$editingTemplate.filename}
          </h2>
        {:else if $selectedDoc}
          <span class="shorttitle-label">{$i18n('notes.shorttitle')}</span>
          {#if isEditingShorttitle}
            <input
              bind:this={shorttitleInputRef}
              bind:value={editedShorttitle}
              on:keydown={handleShorttitleKeydown}
              on:blur={saveShorttitle}
              class="title-input"
              placeholder={$i18n('notes.shorttitlePlaceholder')}
            />
          {:else}
            <h2 class="doc-title-editable" class:shorttitle-empty={!currentShorttitle} on:click={startEditingShorttitle} title={$i18n('notes.clickToEditShorttitle')}>
              {currentShorttitle || $i18n('notes.shorttitlePlaceholder')}
            </h2>
          {/if}
        {:else}
          <h2>{$i18n('notes.title')}</h2>
        {/if}
      </div>

      <div class="header-right">
        <!-- Status -->
        <div class="status">
          {#if $editorState.error}
            <span class="status-error" title={$editorState.error}>
              ⚠️ {$i18n('notes.error')}
            </span>
          {:else if $editorState.isSaving}
            <span class="status-saving">
              💾 {$i18n('notes.saving')}
            </span>
          {:else if $hasUnsavedChanges}
            <span class="status-unsaved">
              • {$i18n('notes.unsavedChanges')}
            </span>
          {:else if $statusMessage}
            <span class="status-saved">
              ✓ {$i18n('notes.saved')}
            </span>
          {/if}
        </div>

        <!-- Actions -->
        <div class="header-actions">
          {#if isSourceOnlyMode}
            <!-- Source-only mode: only fullscreen -->
            <button
              class="btn-icon"
              on:click={toggleFullscreen}
              title={$i18n('notes.fullscreen')}
            >
              {isFullscreen ? '⛶' : '⛶'}
            </button>
          {:else}
            <!-- Document Type Selector -->
            {#if $selectedDoc}
              <select
                class="document-type-selector"
                value={$selectedDoc.documentType || ''}
                on:change={handleDocumentTypeChange}
                title={$i18n('notes.documentType')}
              >
                <option value="">{$i18n('notes.documentType')}</option>
                {#each Object.entries(DOCUMENT_TYPES) as [category, data]}
                  <optgroup label={$i18n(`docs.section.${category}`)}>
                    {#each data.types as type}
                      <option value={type.value}>{$i18n(`docType.${type.value}`)}</option>
                    {/each}
                  </optgroup>
                {/each}
              </select>
            {/if}

            <!-- Export dropdown -->
            <div class="export-dropdown">
              <button
                class="btn-icon"
                on:click={toggleExportMenu}
                disabled={isExporting || !$selectedDoc}
                title={$i18n('notes.export')}
              >
                {isExporting ? '⏳' : '📥'}
              </button>
              {#if showExportMenu}
                <div class="export-menu">
                  <button class="export-option" on:click={() => handleExport('md')}>
                    📄 {$i18n('notes.export.markdown')}
                  </button>
                  <button class="export-option" on:click={() => handleExport('docx')}>
                    📘 {$i18n('notes.export.word')}
                  </button>
                  <button class="export-option" on:click={() => handleExport('odt')}>
                    📗 {$i18n('notes.export.odt')}
                  </button>
                  <button class="export-option" on:click={() => handleExport('pdf')}>
                    📕 {$i18n('notes.export.pdf')}
                  </button>
                  <button class="export-option" on:click={() => handleExport('html')}>
                    🌐 {$i18n('notes.export.html')}
                  </button>
                </div>
              {/if}
            </div>

            <!-- Split document button -->
            <button
              class="btn-icon"
              on:click={handleSplitDocument}
              disabled={isSplitting || !$selectedDoc}
              title={$i18n('notes.split')}
            >
              {isSplitting ? '⏳' : '✂️'}
            </button>

            <button
              class="btn-icon"
              on:click={toggleFullscreen}
              title={$i18n('notes.fullscreen')}
            >
              {isFullscreen ? '⛶' : '⛶'}
            </button>

            <button
              class="btn-icon"
              class:active={showMarkdownSource}
              on:click={toggleMarkdownSource}
              title={$i18n('notes.toggleSource')}
            >
              {showMarkdownSource ? '📝' : '</>'}
            </button>

            <button
              class="btn-icon"
              class:active={showNavigator}
              on:click={toggleNavigator}
              title={$i18n('notes.toggleNavigator')}
              disabled={showMarkdownSource}
            >
              📑
            </button>
          {/if}
        </div>
      </div>
    </div>

  <!-- Contenu de l'éditeur -->
  <div class="notes-content" class:with-navigator={showNavigator && !showMarkdownSource && !isSourceOnlyMode}>
    <div class="editor-area">
      {#if $selectedDoc || $editingTemplate}
        {#if $editorState.isLoading}
          <div class="loading-state">
            <div class="spinner"></div>
            <p>{$i18n('notes.loading')}</p>
          </div>
        {:else}
          {#if isSourceOnlyMode}
            <!-- Source-only mode for non-markdown templates (CSS, Lua, CSL) -->
            <div class="source-only-container">
              {#await CodeMirrorEditorPromise then module}
                {#key $currentDocId}
                  <svelte:component
                    this={module.default}
                    content={markdownSourceText}
                    language={$editingTemplate?.templateType || 'css'}
                    on:change={(e) => handleSourceOnlyChange(e.detail)}
                  />
                {/key}
              {:catch}
                <!-- Fallback to textarea if CodeMirror fails to load -->
                <textarea
                  class="markdown-source"
                  value={markdownSourceText}
                  on:input={handleSourceUpdate}
                  placeholder={$i18n('notes.sourcePlaceholder')}
                  spellcheck="false"
                />
              {/await}
            </div>
          {:else if showMarkdownSource}
            <!-- Vue source Markdown -->
            <div class="markdown-source-container">
              <div class="source-header">
                <span class="source-label">📄 {$i18n('notes.sourceMarkdown')}</span>
              </div>
              <textarea
                class="markdown-source"
                value={markdownSourceText}
                on:input={handleSourceUpdate}
                placeholder={$i18n('notes.sourcePlaceholder')}
                spellcheck="false"
              />
            </div>
          {:else}
            <!-- Toolbar d'édition -->
            <EditorToolbar />

            <!-- Éditeur Milkdown with Error Boundary -->
            <div class="editor-container">
              <ErrorBoundary
                componentName="Milkdown Editor"
                showDetails={true}
                allowRetry={true}
                on:error={(e) => console.error('Editor error:', e.detail)}
              >
                {#key $currentDocId}
                  <MilkdownEditor
                    docId={$currentDocId}
                    initialContent={$loadedContent}
                    placeholder={$i18n('notes.editorPlaceholder')}
                    documentType={$editingTemplate?.documentType || $selectedDoc?.documentType}
                  />
                {/key}
              </ErrorBoundary>
            </div>
          {/if}
        {/if}
      {:else}
        <div class="empty-state">
          <div class="empty-icon">📝</div>
          <p>{$i18n('notes.selectDoc')}</p>
        </div>
      {/if}
    </div>

    <!-- Navigator Panel -->
    {#if showNavigator && !showMarkdownSource && $selectedDoc}
      <div class="navigator-panel">
        <div class="navigator-header">
          <h3>{$i18n('notes.navigator')}</h3>
          <button
            class="btn-icon btn-close-navigator"
            on:click={toggleNavigator}
            title={$i18n('notes.closeNavigator')}
          >
            ✕
          </button>
        </div>

        <!-- Navigator Tabs -->
        <div class="navigator-tabs">
          <button
            class="navigator-tab"
            class:active={navigatorTab === 'toc'}
            on:click={() => navigatorTab = 'toc'}
          >
            {$i18n('notes.navTabToc')}
            <span class="tab-count">{tocHeadings.length}</span>
          </button>
          <button
            class="navigator-tab"
            class:active={navigatorTab === 'bibliography'}
            on:click={() => navigatorTab = 'bibliography'}
          >
            {$i18n('notes.navTabBibliography')}
            {#if $zoteroTotalItems > 0}
              <span class="tab-count">{$zoteroTotalItems}</span>
            {/if}
          </button>
          <button
            class="navigator-tab"
            class:active={navigatorTab === 'style'}
            on:click={() => navigatorTab = 'style'}
          >
            {$i18n('notes.navTabStyle')}
            <span class="tab-count">{divBlocks.length + inlineStyles.length}</span>
          </button>
          <button
            class="navigator-tab"
            class:active={navigatorTab === 'placeholders'}
            on:click={() => navigatorTab = 'placeholders'}
          >
            {$i18n('notes.navTabPlaceholders')}
            <span class="tab-count">{placeholderCount}</span>
          </button>
        </div>

        <!-- Style Sub-tabs (outside scrollable area, like BibliographyPanel) -->
        {#if navigatorTab === 'style'}
          <div class="style-subtabs">
            <button
              class="tab-btn"
              class:active={styleSubTab === 'paragraph'}
              on:click={() => styleSubTab = 'paragraph'}
            >
              {$i18n('notes.navSubTabParagraph')}
              <span class="tab-count">{divBlocks.length}</span>
            </button>
            <button
              class="tab-btn"
              class:active={styleSubTab === 'character'}
              on:click={() => styleSubTab = 'character'}
            >
              {$i18n('notes.navSubTabCharacter')}
              <span class="tab-count">{inlineStyles.length}</span>
            </button>
          </div>
        {/if}

        <div class="navigator-content" class:navigator-content-full={navigatorTab === 'bibliography'}>
          {#if navigatorTab === 'toc'}
            <!-- TOC Tab -->
            {#if tocHeadings.length === 0}
              <div class="navigator-empty">
                <p>{$i18n('notes.tocEmpty')}</p>
              </div>
            {:else}
              <ul class="toc-list">
                {#each tocHeadings as heading (heading.id)}
                  <li class="toc-item toc-level-{heading.level}">
                    <button
                      class="toc-link"
                      on:click={() => scrollToHeading(heading)}
                      title={heading.text}
                    >
                      {heading.text}
                    </button>
                  </li>
                {/each}
              </ul>
            {/if}
          {:else if navigatorTab === 'style'}
            <!-- Style Tab content -->
            {#if styleSubTab === 'paragraph'}
              <!-- Paragraph (Blocks) Sub-Tab -->
              {#if divBlocks.length === 0}
                <div class="navigator-empty">
                  <p>{$i18n('notes.blocksEmpty')}</p>
                </div>
              {:else}
                <ul class="blocks-list">
                  {#each divBlocks as block (block.id)}
                    <li class="block-item">
                      <button
                        class="block-link"
                        on:click={() => scrollToDivBlock(block)}
                        title={block.preview || block.name}
                      >
                        <span class="block-name">{block.name}</span>
                        {#if block.preview}
                          <span class="block-preview">{block.preview}</span>
                        {/if}
                      </button>
                    </li>
                  {/each}
                </ul>
              {/if}
            {:else}
              <!-- Character (Styles) Sub-Tab -->
              {#if inlineStyles.length === 0}
                <div class="navigator-empty">
                  <p>{$i18n('notes.stylesEmpty')}</p>
                </div>
              {:else}
                <ul class="styles-list">
                  {#each inlineStyles as style (style.id)}
                    <li class="style-item">
                      <button
                        class="style-link"
                        on:click={() => scrollToInlineStyle(style)}
                        title={style.text}
                      >
                        <span class="style-name">.{style.className}</span>
                        <span class="style-count">{style.count}×</span>
                        {#if style.text}
                          <span class="style-preview">{style.text}</span>
                        {/if}
                      </button>
                    </li>
                  {/each}
                </ul>
              {/if}
            {/if}
          {:else if navigatorTab === 'placeholders'}
            <!-- Placeholders Tab -->
            {#if placeholderItems.length === 0}
              <div class="navigator-empty">
                <p>{$i18n('notes.placeholdersEmpty')}</p>
              </div>
            {:else}
              {#each placeholderCategoryLabels as catLabel}
                <div class="placeholder-category">
                  <h4 class="placeholder-category-label">{catLabel}</h4>
                  <ul class="placeholder-list">
                    {#each placeholderItems.filter(p => p.category === catLabel) as ph (ph.name)}
                      <li class="placeholder-item">
                        <button
                          class="placeholder-link"
                          title="Double-clic pour insérer {ph.token}"
                          on:dblclick={() => insertPlaceholder(ph.token)}
                        >
                          <span class="placeholder-token">{ph.token}</span>
                          {#if ph.value}
                            <span class="placeholder-value">{ph.value.length > 40 ? ph.value.slice(0, 37) + '...' : ph.value}</span>
                          {:else}
                            <span class="placeholder-value placeholder-value-empty">—</span>
                          {/if}
                        </button>
                      </li>
                    {/each}
                  </ul>
                </div>
              {/each}
            {/if}
          {:else if navigatorTab === 'bibliography'}
            <!-- Bibliography Tab -->
            <BibliographyPanel on:insertCitation={handleInsertCitation} />
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .notes-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--panel-background);
    border-left: 1px solid var(--border-color);
    transition: all 0.3s ease;
  }

  .notes-panel.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    border: none;
  }

  /* Header */
  .notes-header {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
    background: var(--panel-background);
    min-height: 60px;
    gap: 0.5rem;
  }

  .notes-header-collapsed {
    display: flex;
    justify-content: center;
    padding: 0.5rem;
    border-bottom: 1px solid var(--border-color);
    background: var(--panel-background);
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0;
    flex: 1 1 auto;
  }

  .header-left h2 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .shorttitle-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    white-space: nowrap;
  }

  .doc-title-editable {
    cursor: pointer;
    transition: color 0.2s;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .doc-title-editable:hover {
    color: var(--accent-color);
  }

  .shorttitle-empty {
    font-style: italic;
    color: var(--text-muted);
    font-weight: 400;
  }

  .template-title {
    cursor: default;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .template-badge {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    background: color-mix(in srgb, var(--accent-color, #3b82f6) 15%, transparent);
    color: var(--accent-color, #3b82f6);
    white-space: nowrap;
  }

  .title-input {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--text-primary);
    background: var(--panel-background);
    border: 2px solid var(--accent-color);
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    outline: none;
    min-width: 300px;
    max-width: 500px;
  }

  .doc-title {
    color: var(--text-muted);
    font-size: 0.9rem;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    flex-wrap: wrap;
  }

  /* Status */
  .status {
    font-size: 0.85rem;
    text-align: right;
    white-space: nowrap;
  }

  .status-error {
    color: var(--error-color, #bf616a);
  }

  .status-saving {
    color: var(--warning-color, #ebcb8b);
  }

  .status-unsaved {
    color: var(--warning-color, #ebcb8b);
  }

  .status-saved {
    color: var(--success-color, #a3be8c);
  }

  /* Actions */
  .header-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  /* Document Type Selector */
  .document-type-selector {
    padding: 0.4rem 0.75rem;
    background: var(--panel-background, white);
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.85rem;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.2s;
    min-width: 160px;
  }

  .document-type-selector:hover {
    border-color: var(--accent-color);
  }

  .document-type-selector:focus {
    outline: none;
    border-color: var(--accent-color);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  .document-type-selector option {
    padding: 0.5rem;
  }

  .document-type-selector optgroup {
    font-weight: 600;
    font-size: 0.8rem;
    color: var(--text-muted);
    padding: 0.25rem 0;
  }

  /* Export dropdown */
  .export-dropdown {
    position: relative;
  }

  .export-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 0.25rem;
    background: var(--panel-background, white);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 100;
    min-width: 180px;
    overflow: hidden;
  }

  .export-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.75rem 1rem;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 0.9rem;
    color: var(--text-primary);
    text-align: left;
    transition: background 0.15s;
  }

  .export-option:hover {
    background: var(--hover-background, #f3f4f6);
  }

  .export-option:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }

  .btn-icon {
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    font-size: 1rem;
    transition: all 0.2s;
    color: var(--text-primary);
  }

  .btn-icon:hover:not(:disabled) {
    background: var(--hover-background);
    border-color: var(--accent-color);
  }

  .btn-icon:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-icon.active {
    background: var(--accent-color);
    color: white;
    border-color: var(--accent-color);
  }

  .btn-small {
    background: var(--accent-color);
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.2s;
    font-weight: 500;
  }

  .btn-small:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .btn-small:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Content */
  .notes-content {
    flex: 1;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .editor-container {
    flex: 1;
    overflow: hidden;
  }

  /* Markdown Source View */
  .markdown-source-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .source-only-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .source-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--border-color);
    background: var(--panel-background);
  }

  .source-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .source-type-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    background: var(--accent-color, #5e81ac);
    color: white;
  }

  .markdown-source {
    flex: 1;
    width: 100%;
    padding: 1rem;
    border: none;
    outline: none;
    resize: none;
    background: var(--code-background, #2e3440);
    color: var(--code-text, #d8dee9);
    font-family: 'Fira Code', 'Monaco', 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.6;
    tab-size: 2;
    overflow-y: auto;
  }

  .markdown-source::placeholder {
    color: var(--text-muted);
    opacity: 0.5;
  }

  .markdown-source:focus {
    background: var(--code-background, #2e3440);
  }

  /* Empty state */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: var(--text-muted);
    gap: 1rem;
  }

  .empty-icon {
    font-size: 3rem;
    opacity: 0.5;
  }

  .empty-state p {
    margin: 0;
    font-size: 1rem;
  }

  /* Loading state */
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 1rem;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-state p {
    margin: 0;
    color: var(--text-muted);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .doc-title-editable {
      font-size: 1rem;
    }

    .title-input {
      min-width: 150px;
      max-width: 100%;
      font-size: 1rem;
    }

    .header-left h2 {
      font-size: 1rem;
    }

    .notes-header {
      padding: 0.75rem;
    }

    .header-left {
      flex: 1 1 100%;
    }

    .status {
      min-width: auto;
    }
  }

  /* Navigator Panel */
  .notes-content.with-navigator {
    flex-direction: row;
    overflow: hidden;
  }

  .editor-area {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* When Navigator is shown, editor-container needs to fill available space */
  .notes-content.with-navigator .editor-container {
    flex: 1;
    overflow: auto;
    min-height: 0;
  }

  .navigator-panel {
    width: 250px;
    min-width: 200px;
    max-width: 300px;
    background: var(--panel-background);
    border-left: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
  }

  .navigator-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid var(--border-color);
    background: var(--hover-background);
  }

  .navigator-header h3 {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .btn-close-navigator {
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
  }

  /* Navigator Tabs */
  .navigator-tabs {
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid var(--border-color);
  }

  .navigator-tab {
    flex: 0 0 auto;
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    padding: 0.4rem 0.5rem;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--text-muted);
    font-size: 0.7rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .navigator-tab:hover {
    color: var(--text-primary);
    background: var(--hover-background);
  }

  .navigator-tab.active {
    color: var(--accent-color);
    border-bottom-color: var(--accent-color);
  }

  .navigator-tab .tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1rem;
    padding: 0 0.3rem;
    background: var(--bg-tertiary);
    border-radius: 9999px;
    font-size: 0.65rem;
    font-weight: 600;
  }

  .navigator-tab.active .tab-count {
    background: var(--accent-color-light);
    color: var(--accent-color);
  }

  /* Style Sub-tabs (matching BibliographyPanel Local/Zotero tabs) */
  .style-subtabs {
    display: flex;
    border-bottom: 1px solid var(--border-color);
    background: var(--panel-background);
  }

  .style-subtabs .tab-btn {
    flex: 1;
    padding: 0.5rem 0.75rem;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
  }

  .style-subtabs .tab-btn:hover {
    color: var(--text-primary);
    background: var(--hover-background);
  }

  .style-subtabs .tab-btn.active {
    color: var(--accent-color);
    border-bottom-color: var(--accent-color);
  }

  .style-subtabs .tab-count {
    font-size: 0.65rem;
    background: var(--accent-color);
    color: white;
    padding: 0.1rem 0.35rem;
    border-radius: 8px;
    min-width: 1.2rem;
    text-align: center;
  }

  .navigator-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
  }

  .navigator-content-full {
    padding: 0;
    overflow: hidden;
  }

  .navigator-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: var(--text-muted);
    font-size: 0.8rem;
    text-align: center;
    padding: 1rem;
  }

  /* TOC List */
  .toc-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .toc-item {
    margin: 0;
    padding: 0;
  }

  .toc-link {
    display: block;
    width: 100%;
    padding: 0.4rem 0.5rem;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: var(--text-primary);
    font-size: 0.85rem;
    text-align: left;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .toc-link:hover {
    background: var(--hover-background);
    color: var(--accent-color);
  }

  /* Indentation based on heading level */
  .toc-level-1 .toc-link {
    font-weight: 600;
    padding-left: 0.5rem;
  }

  .toc-level-2 .toc-link {
    font-weight: 500;
    padding-left: 1rem;
  }

  .toc-level-3 .toc-link {
    font-weight: 400;
    padding-left: 1.5rem;
    font-size: 0.8rem;
  }

  .toc-level-4 .toc-link {
    font-weight: 400;
    padding-left: 2rem;
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  /* Blocks List */
  .blocks-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .block-item {
    margin: 0;
    padding: 0;
  }

  .block-link {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0.5rem;
    background: transparent;
    border: none;
    border-radius: 4px;
    text-align: left;
    cursor: pointer;
    transition: all 0.15s;
    gap: 0.2rem;
  }

  .block-link:hover {
    background: var(--hover-background);
  }

  .block-name {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--accent-color);
    font-family: var(--font-family-mono);
  }

  .block-preview {
    font-size: 0.75rem;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Styles List */
  .styles-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .style-item {
    margin: 0;
    padding: 0;
  }

  .style-link {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0.5rem;
    background: transparent;
    border: none;
    border-radius: 4px;
    text-align: left;
    cursor: pointer;
    transition: all 0.15s;
    gap: 0.2rem;
  }

  .style-link:hover {
    background: var(--hover-background);
  }

  .style-name {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--accent-color);
    font-family: var(--font-family-mono);
  }

  .style-count {
    font-size: 0.7rem;
    color: var(--text-muted);
    background: var(--bg-tertiary);
    padding: 0.1rem 0.3rem;
    border-radius: 4px;
    display: inline-block;
    margin-left: 0.5rem;
  }

  .style-preview {
    font-size: 0.75rem;
    color: var(--text-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Placeholder List */
  .placeholder-category {
    margin-bottom: 0.5rem;
  }

  .placeholder-category-label {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.3rem 0.5rem;
    margin: 0;
  }

  .placeholder-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .placeholder-item {
    margin: 0;
    padding: 0;
  }

  .placeholder-link {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 0.4rem 0.5rem;
    background: transparent;
    border: none;
    border-radius: 4px;
    text-align: left;
    cursor: pointer;
    transition: all 0.15s;
    gap: 0.15rem;
  }

  .placeholder-link:hover {
    background: var(--hover-background);
  }

  .placeholder-token {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--accent-color);
    font-family: var(--font-family-mono);
  }

  .placeholder-value {
    font-size: 0.75rem;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .placeholder-value-empty {
    color: var(--text-muted);
    font-style: italic;
  }

  /* Heading/Block highlight animation when scrolled to */
  :global(.toc-highlight) {
    background: var(--accent-color-light) !important;
    transition: background 0.3s ease;
  }

  /* NOTE: Les variables CSS sont définies globalement dans variables.css */
</style>
